import angularModule from 'LearnerProjects/angularModule/scripts/learner_projects_module';
import hasDateAsTimestampInJson from 'hasDateAsTimestampInJson';

angularModule.factory('ProjectProgress', [
    '$injector',
    function ($injector) {
        const Iguana = $injector.get('Iguana');
        const ProjectProgressTimeline = $injector.get('ProjectProgressTimeline');

        return Iguana.subclass(function () {
            this.setCollection('project_progress');
            this.alias('ProjectProgress');
            this.setIdProperty('id');

            this.setCallback('after', 'copyAttrsOnInitialize', function () {
                this.historical_project_submissions = this.historical_project_submissions || [];
            });

            // Configure date-to-timestamp conversion for resubmissionDueDate
            hasDateAsTimestampInJson(this, 'resubmissionDueDate');

            Object.defineProperty(this.prototype, 'scoreOptionValue', {
                get() {
                    if (this.resubmission_required) {
                        return 'resubmission_required';
                    }
                    if (this.waived) {
                        return 'waived';
                    }
                    if (this.score === undefined) {
                        return null;
                    }
                    return this.score;
                },
                set(val) {
                    // If we're setting a score, we should unset the provisional score:
                    this.provisional_score = null;

                    if (val === 'resubmission_required') {
                        this.resubmission_required = true;
                        this.waived = false;
                        this.score = 0;
                        this.allow_resubmission = true;
                        // Unlike waived, resubmission_required keeps status as-is
                    } else if (val === 'waived') {
                        this.resubmission_required = false;
                        this.allow_resubmission = false;
                        this.waived = true;
                        this.score = null;
                        this.status = null;
                    } else if (val === null || val === undefined) {
                        this.resubmission_required = false;
                        this.waived = false;
                        this.score = null;
                    } else {
                        this.resubmission_required = false;
                        this.waived = false;
                        this.score = Number(val);

                        // If we are explicitly setting the `score` column, we should
                        // automatically update the `status` column to submitted if it
                        // does not yet have a value.
                        this.status = this.status || 'submitted';
                    }
                },
            });

            // Clearing is a no-op when there is no submission: the `scoreOptionValue` setter clears the
            // provisional grade on every write, including for a progress record that was never submitted.
            // Writing a real value with nowhere to put it is a bug, so fail loudly instead of silently.
            function setProvisionalOnSubmission(progress, attr, val) {
                if (progress.project_submission) {
                    progress.project_submission[attr] = val;
                    return;
                }
                if (val !== null && val !== undefined) {
                    throw new Error(`Cannot set ${attr} on a ProjectProgress with no project_submission`);
                }
            }

            Object.defineProperty(this.prototype, 'provisional_score', {
                get() {
                    return this.project_submission ? this.project_submission.provisional_score : undefined;
                },
                set(val) {
                    setProvisionalOnSubmission(this, 'provisional_score', val);
                },
            });

            Object.defineProperty(this.prototype, 'provisional_feedback', {
                get() {
                    return this.project_submission ? this.project_submission.provisional_feedback : undefined;
                },
                set(val) {
                    setProvisionalOnSubmission(this, 'provisional_feedback', val);
                },
            });

            Object.defineProperty(this.prototype, 'provisionalScoreOptionValue', {
                get() {
                    // delegating to scoreOptionValue getter to handle the cases where
                    // we've been waived (if there's an actual score set, we don't consider
                    // it provisional, so it shouldn't be relevant)
                    if (this.scoreOptionValue !== null) {
                        return this.scoreOptionValue;
                    }
                    // When resubmission_required is set provisionally, score is null
                    // so scoreOptionValue returns null — handle it here
                    if (this.resubmission_required) {
                        return 'resubmission_required';
                    }
                    if (this.provisional_score === undefined) {
                        return null;
                    }
                    return this.provisional_score;
                },
                set(val) {
                    if (val === 'waived') {
                        // Rely on the scoreOptionValue setter to handle waived,
                        // since it switches us to not being considered provisional
                        this.scoreOptionValue = val;
                        return;
                    }
                    if (val === 'resubmission_required') {
                        // Handle like a numeric score: stay provisional so the
                        // approval checkbox remains visible
                        this.resubmission_required = true;
                        this.waived = false;
                        this.provisional_score = 0;
                        this.score = null;
                        this.status = this.status || 'submitted';
                        return;
                    }
                    if (val === null || val === undefined) {
                        this.resubmission_required = false;
                        this.provisional_score = null;
                        return;
                    }
                    this.resubmission_required = false;
                    this.provisional_score = Number(val);

                    // If we are explicitly setting the `provisional_score` column, we should
                    // automatically update the `status` column to submitted if it
                    // does not yet have a value.
                    this.status = this.status || 'submitted';
                },
            });

            Object.defineProperty(this.prototype, 'hasScoreSet', {
                get() {
                    return !!this.score || this.score === 0;
                },
            });

            Object.defineProperty(this.prototype, 'hasProvisionalScoreSet', {
                get() {
                    return this.isProvisional && (!!this.provisional_score || this.provisional_score === 0);
                },
            });

            // Note: this returns true for cases where no score is set in either field, which is intentional
            // for how this property is USED, but perhaps not ideal for how it's named.  But something like
            // `hasNoApprovedGradebookDisplayedGradingState` feels even more confusing.  Consider using `hasProvisionalScoreSet`.
            Object.defineProperty(this.prototype, 'isProvisional', {
                get() {
                    return !this.hasScoreSet && !this.waived;
                },
            });

            // Not to be confused with the `status` column (which can be set by admins);
            // this is a calculated property that intends to show where we are in the flow
            // of the grading process.
            Object.defineProperty(this.prototype, 'gradebookDisplayedGradingState', {
                get() {
                    if (this.resubmission_required) {
                        return 'resubmission required';
                    }
                    if (this.allow_resubmission) {
                        return 'awaiting resubmission';
                    }
                    if (this.hasScoreSet) {
                        return 'graded';
                    }
                    if (this.hasProvisionalScoreSet) {
                        return 'provisional';
                    }
                    // When we support auto-grading, we might want to show something like
                    // "awaiting auto-grading" here (if the project supports that).
                    if (this.status === 'submitted') {
                        return 'submitted';
                    }
                    if (this.status) {
                        return this.status; // e.g. 'plagiarized'; also a defensive fallback for any unexpected status
                    }
                    // null (never submitted) and waived have no grading state
                    return '-';
                },
            });

            return {
                // Duplicated on server model
                satisfiesRequirement(project) {
                    if (this.waived) return true;
                    if (project.isPresentationProject && !this.id_verified) {
                        return false;
                    }
                    return !!this.passed;
                },

                // this could be a getter, but it pairs with the function below, so
                // I left it here.
                // These are tested in admin_cohort_gradebook_dir_spec.js
                gradebookDisplayedScore() {
                    if (this.scoreOptionValue === 'resubmission_required') {
                        return 0;
                    }
                    if (this.scoreOptionValue === 'waived') {
                        return 'Waived';
                    }

                    if (this.scoreOptionValue === null) {
                        if (this.provisional_score === 0) {
                            return '0';
                        }
                        return this.provisional_score || '-';
                    }
                    return this.scoreOptionValue;
                },

                gradebookDisplayedScoreCssClass(project) {
                    if (this.isProvisional) {
                        return 'provisional';
                    }
                    if (!this.satisfiesRequirement(project)) {
                        return 'unpassed';
                    }
                    return 'passed'; // also includes waived
                },

                getCorrespondingTimelineEvents() {
                    return ProjectProgressTimeline.show(this.id).then(
                        response => (response.result && response.result.events) || [],
                    );
                },

                objectForSave($super) {
                    const json = $super();
                    json.project_submission_similarity_and_originality_status =
                        this.project_submission_similarity_and_originality_status;
                    json.project_submission_needs_admin_review = this.project_submission_needs_admin_review;
                    json.project_submission_private_notes = this.project_submission_private_notes;
                    json.project_submission_presentation_transcript = this.project_submission_presentation_transcript;

                    // Only send it when the nested submission actually carries provisional (the cohort_gradebook
                    // serialization), so other save contexts don't touch it.
                    if (this.project_submission && 'provisional_score' in this.project_submission) {
                        json.project_submission_provisional_score = this.project_submission.provisional_score ?? null;
                        json.project_submission_provisional_feedback =
                            this.project_submission.provisional_feedback ?? null;
                    }

                    return json;
                },
            };
        });
    },
]);
